import { ClientError } from "graphql-request";

type SessionExpiredListener = () => void;

let sessionExpired = false;
let inFlightFreshness: Promise<boolean> | null = null;
let recovering = false;
const expiredListeners = new Set<SessionExpiredListener>();

const AUTH_ERROR_MESSAGES = new Set(["unauthenticated.", "unauthenticated", "unauthorized.", "unauthorized"]);

/**
 * Nudge Laravel `AuthenticateWithWorkos` via GET /api/auth/status so an expired
 * access token can rotate the sealed session cookie when a refresh token is still valid.
 * Concurrent callers share one in-flight request (single-flight).
 */
export const ensureSessionFresh = async (): Promise<boolean> => {
  if (sessionExpired) {
    return false;
  }

  if (inFlightFreshness) {
    return inFlightFreshness;
  }

  recovering = true;
  inFlightFreshness = (async () => {
    try {
      const res = await fetch("/api/auth/status", {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
        signal: AbortSignal.timeout(20_000),
      });

      if (res.ok) {
        return true;
      }

      // Only latch hard expiry on auth failure — 5xx/network should not force login.
      if (res.status === 401 || res.status === 403) {
        markSessionExpired();
      }
      return false;
    } catch {
      // Network blip — do not mark expired; caller may retry later.
      return false;
    } finally {
      recovering = false;
      inFlightFreshness = null;
    }
  })();

  return inFlightFreshness;
};

export const isSessionRecoveryInFlight = (): boolean => recovering || inFlightFreshness !== null;

export const isSessionExpired = (): boolean => sessionExpired;

export const markSessionExpired = (): void => {
  if (sessionExpired) {
    return;
  }
  sessionExpired = true;
  for (const listener of expiredListeners) {
    try {
      listener();
    } catch {
      // Listeners must not break each other.
    }
  }
};

/** Test / logout helper — clears the expired latch so a new login can succeed. */
export const clearSessionExpired = (): void => {
  sessionExpired = false;
};

export const subscribeSessionExpired = (listener: SessionExpiredListener): (() => void) => {
  expiredListeners.add(listener);
  if (sessionExpired) {
    listener();
  }
  return () => {
    expiredListeners.delete(listener);
  };
};

export const isUnauthorizedHttpError = (error: unknown): boolean => {
  if (error instanceof ClientError) {
    return error.response.status === 401;
  }

  if (!error || typeof error !== "object" || !("response" in error)) {
    return false;
  }

  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== "object") {
    return false;
  }

  return (response as { status?: unknown }).status === 401;
};

export const isAuthError = (error: unknown): boolean => {
  if (isUnauthorizedHttpError(error)) {
    return true;
  }

  if (error instanceof ClientError) {
    const message = error.response?.errors?.[0]?.message?.trim().toLowerCase() ?? "";
    if (AUTH_ERROR_MESSAGES.has(message)) {
      return true;
    }
  }

  if (error instanceof Error) {
    const message = error.message.trim().toLowerCase();
    if (AUTH_ERROR_MESSAGES.has(message) || message.includes("unauthenticated")) {
      return true;
    }
  }

  return false;
};

/** Call sites should skip toasts — redirect / session heal owns the UX. */
export const shouldSuppressAuthToast = (error?: unknown): boolean => {
  if (sessionExpired || recovering || inFlightFreshness !== null) {
    return true;
  }
  if (error !== undefined && isAuthError(error)) {
    return true;
  }
  return false;
};

export const isTabVisible = (): boolean => {
  if (typeof document === "undefined") {
    return true;
  }
  return document.visibilityState === "visible";
};
